import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import type { Principal } from '@ampshift/shared';
import { API_BASE_URL, getSessionId, type AuthHeaders } from './api';

export interface AuthSession {
  headers: AuthHeaders;
  displayName: string;
  patientId?: string;
}

export const DEV_AUTH: boolean = String(import.meta.env.VITE_DEV_AUTH ?? '') === '1';

/** Mirrors `DEMO_PRINCIPALS.patient` from @ampshift/seed; used only when /dev/principals is unreachable. */
const FALLBACK_DEMO_PATIENT: Principal = {
  tenantId: 'tenant_demo', userId: 'user_patient_1', roles: ['PATIENT'], patientId: 'pat_001', sessionId: 'dev-session', locale: 'en-US', displayName: 'Mohammed Al-Otaibi',
};

function isPrincipal(v: unknown): v is Principal {
  return typeof v === 'object' && v !== null && typeof (v as Principal).userId === 'string' && Array.isArray((v as Principal).roles);
}

/** Dev mode: pick the `patient` principal from the local API's /dev/principals listing. */
export async function devLogin(fetchImpl: typeof fetch = (i, o) => fetch(i, o)): Promise<AuthSession> {
  let principal: Principal = FALLBACK_DEMO_PATIENT;
  try {
    const res = await fetchImpl(`${API_BASE_URL}/dev/principals`);
    if (res.ok) {
      const body = (await res.json()) as { principals?: Record<string, unknown> } | Record<string, unknown> | unknown[];
      const pool: unknown[] = Array.isArray(body) ? body : Object.values(((body as { principals?: Record<string, unknown> }).principals ?? body) as Record<string, unknown>);
      const named = !Array.isArray(body) ? ((body as { principals?: Record<string, unknown> }).principals ?? (body as Record<string, unknown>))['patient'] : undefined;
      const found = isPrincipal(named) ? named : pool.find((p): p is Principal => isPrincipal(p) && p.roles.includes('PATIENT'));
      if (found) principal = found;
    }
  } catch {
    /* API not running: fall back to the fixed demo principal */
  }
  principal = { ...principal, sessionId: getSessionId() };
  return { headers: { kind: 'dev', principal }, displayName: principal.displayName ?? 'Demo patient', patientId: principal.patientId };
}

/** Cognito SRP sign-in (no passwords leave the browser in clear). */
export function cognitoLogin(email: string, password: string): Promise<AuthSession> {
  const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
  const ClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
  if (!UserPoolId || !ClientId) return Promise.reject(new Error('VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID are not configured'));
  const pool = new CognitoUserPool({ UserPoolId, ClientId });
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
      onSuccess: (session) => {
        const idToken = session.getIdToken();
        const payload = idToken.payload as Record<string, unknown>;
        resolve({
          headers: { kind: 'cognito', idToken: idToken.getJwtToken() },
          displayName: String(payload['name'] ?? payload['email'] ?? email),
          patientId: typeof payload['custom:patientId'] === 'string' ? (payload['custom:patientId'] as string) : undefined,
        });
      },
      onFailure: (err: Error) => reject(err),
      newPasswordRequired: () => reject(new Error('A new password is required; please complete sign-up in the staff app first.')),
    });
  });
}

export function cognitoLogout(): void {
  const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
  const ClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
  if (!UserPoolId || !ClientId) return;
  new CognitoUserPool({ UserPoolId, ClientId }).getCurrentUser()?.signOut();
}
